<div class="ws-step">
  <div class="ws-step-header">
    <h3>Add Initial Leads</h3>
    <p class="text-muted">There are many integrations available to connect your lead sources — Facebook, IndiaMART, JustDial, Housing.com, WebForms, APIs and more. You can set those up later from Customize > Integrations. For now, let's get you started quickly.</p>
  </div>

  <div class="ws-step-body">
    @switch (mode) {
      @case ('choose') {
        <div class="d-flex flex-wrap gap-3">
          <!-- Option 1: Connect Facebook -->
          <div class="ws-option-card" (click)="selectFacebook()">
            <i class="bi bi-facebook" style="color: #1877f2;"></i>
            <div class="ws-option-title">Connect Facebook</div>
            <div class="ws-option-desc">Link your Facebook page to automatically receive leads from lead ads</div>
          </div>

          <!-- Option 2: Upload a Sheet -->
          <div class="ws-option-card" (click)="selectUpload()">
            <i class="bi bi-file-earmark-spreadsheet" style="color: #43a047;"></i>
            <div class="ws-option-title">Upload a Sheet</div>
            <div class="ws-option-desc">Import leads from an Excel or CSV file — we'll auto-detect the columns</div>
          </div>

          <!-- Option 3: AI Test Leads -->
          <div class="ws-option-card" (click)="selectDiscover()">
            <i class="bi bi-stars" style="color: #5c6bc0;"></i>
            <div class="ws-option-title">Find Test Leads</div>
            <div class="ws-option-desc">Let AI find some sample leads for you to try out the CRM with</div>
          </div>
        </div>
      }

      @case ('facebook') {
        @if (!isFbConnected) {
          <!-- Not connected yet -->
          <div class="text-center py-4">
            <i class="bi bi-facebook" style="font-size: 48px; color: #1877f2;"></i>
            <h5 class="mt-3">Connect your Facebook account</h5>
            <p class="text-muted">Sign in with Facebook to link your pages and start receiving leads from lead ads automatically.</p>
            <button class="btn btn-primary" (click)="connectFacebook()" [disabled]="isConnectingFb || isLoadingPages">
              @if (isConnectingFb || isLoadingPages) {
                <span class="spinner-border spinner-border-sm me-1"></span> Connecting...
              } @else {
                <i class="bi bi-facebook me-1"></i> Sign in with Facebook
              }
            </button>
          </div>
        } @else {
          <!-- Connected — show pages -->
          <div class="d-flex align-items-center mb-3">
            <i class="bi bi-check-circle-fill text-success me-2" style="font-size: 20px;"></i>
            <span class="fw-medium">Facebook connected</span>
          </div>

          @if (isLoadingPages) {
            <div class="text-center py-3">
              <span class="spinner-border spinner-border-sm me-1"></span> Loading pages...
            </div>
          } @else if (fbPages.length > 0) {
            <p class="text-muted mb-2">Select the Facebook pages you want to link. Leads from linked pages will automatically flow into your CRM.</p>
            @for (page of fbPages; track page.entityId) {
              <div class="ws-item-card" [ngClass]="{'selected': page.subscribed, 'ws-card-positive': page.subscribed}">
                <div class="ws-item-content">
                  <div class="ws-item-name">
                    <i class="bi bi-facebook me-1" style="color: #1877f2;"></i>
                    {{ page.name }}
                  </div>
                  <div class="ws-item-meta">
                    @if (page.subscribed) {
                      <span class="text-success"><i class="bi bi-check-circle me-1"></i>Linked</span>
                    } @else {
                      <span class="text-muted">Not linked</span>
                    }
                  </div>
                </div>
                <button class="btn btn-sm" [ngClass]="page.subscribed ? 'btn-outline-danger' : 'btn-outline-primary'" (click)="togglePageLink(page)" [disabled]="page.linking">
                  @if (page.linking) {
                    <span class="spinner-border spinner-border-sm"></span>
                  } @else if (page.subscribed) {
                    Unlink
                  } @else {
                    Link
                  }
                </button>
              </div>
            }
            <div class="ws-item-meta mt-2">{{ linkedPagesCount }} page(s) linked. Leads from these pages will automatically appear in your CRM.</div>
          } @else {
            <div class="ws-empty-state">
              <i class="bi bi-facebook"></i>
              <p>No Facebook pages found. Make sure you have a Facebook page with lead forms set up.</p>
            </div>
          }
        }

        <div class="d-flex gap-2 mt-3">
          <button class="btn btn-outline-secondary" (click)="backToChoose()">
            <i class="bi bi-arrow-left me-1"></i>Back
          </button>
          @if (isFbConnected && linkedPagesCount > 0) {
            <button mat-raised-button color="primary" (click)="fbDone()">
              Continue
            </button>
          }
        </div>
      }

      @case ('upload') {
        @if (isMapping) {
          <div class="text-center py-4">
            <div class="spinner-border text-primary mb-3" style="width: 2.5rem; height: 2.5rem;"></div>
            <h6>AI is detecting your columns...</h6>
            <p class="text-muted small">Analyzing your data to auto-map Name, Phone, Email and other fields</p>
          </div>
        } @else {
          <div class="mb-3">
            <label class="form-label fw-medium">Upload a spreadsheet or paste your data</label>
            <div class="ws-upload-area mb-3">
              <i class="bi bi-cloud-arrow-up"></i>
              <p>Click to select a file (CSV, Excel)</p>
              <input type="file" accept=".csv,.txt,.xlsx,.xls" (change)="onFileSelected($event)" />
            </div>

            <label class="form-label small text-muted">Or paste data directly (Name, Phone per line)</label>
            <textarea class="form-control" [(ngModel)]="csvText" rows="6"
              placeholder="John Doe, +919876543210&#10;Jane Smith, +919876543211&#10;..."></textarea>
            <small class="text-muted">Comma or tab separated. One lead per line.</small>
          </div>
          <div class="d-flex gap-2">
            <button class="btn btn-outline-secondary" (click)="backToChoose()">
              <i class="bi bi-arrow-left me-1"></i>Back
            </button>
            <button class="btn btn-primary" (click)="parseCsv()" [disabled]="!csvText.trim()">Parse & Preview</button>
          </div>
        }
      }

      @case ('discover') {
        <div class="mb-3">
          @if (businessDescription) {
            <div class="p-3 bg-light rounded mb-3">
              <small class="text-muted d-block mb-1">We'll find potential customers for:</small>
              <span class="fw-medium">{{ businessDescription }}</span>
            </div>
          }
          <label class="form-label fw-medium">Add any extra details (optional)</label>
          <textarea class="form-control" [(ngModel)]="discoveryPrompt" rows="3"
            placeholder="e.g. Focus on leads in South Delhi, or only businesses with 50+ employees"></textarea>
          <small class="text-muted">AI will search and find potential customers for your business with contact details.</small>
        </div>
        <div class="d-flex gap-2">
          <button class="btn btn-outline-secondary" (click)="backToChoose()">
            <i class="bi bi-arrow-left me-1"></i>Back
          </button>
          <button class="btn btn-primary" (click)="discoverLeads()" [disabled]="isDiscovering">
            @if (isDiscovering) {
              <span class="spinner-border spinner-border-sm me-1"></span> Searching for leads...
            } @else {
              <i class="bi bi-stars me-1"></i> Find Test Leads
            }
          </button>
        </div>
      }

      @case ('review') {
        @if (uploadSource === 'file') {
          <!-- Bulk upload review: show mapping summary + preview -->
          <div class="mb-3">
            <h6 class="text-muted mb-2">{{ totalRowCount }} lead(s) found in your file</h6>
            @if (detectedColumns.length) {
              <div class="mb-3">
                <span class="text-muted small">Detected columns: </span>
                @for (col of detectedColumns; track col.key) {
                  <span class="badge bg-light text-dark border me-1 mb-1">{{ col.mappedTo }}</span>
                }
              </div>
            }
            @if (leads.length) {
              <p class="text-muted small mb-2">Preview:</p>
            }
          </div>
          @for (lead of leads; track $index) {
            <div class="ws-item-card selected">
              <div class="ws-item-content">
                <div class="ws-item-name">{{ lead.name || 'Unknown' }} <span class="text-muted ms-2">{{ lead.phone }}</span></div>
                @if (lead.email) {
                  <div class="ws-item-meta">{{ lead.email }}</div>
                }
                @if (lead.address) {
                  <div class="ws-item-meta">{{ lead.address }}</div>
                }
              </div>
            </div>
          }
          @if (leads.length < totalRowCount) {
            <div class="text-muted small mt-1">...and {{ totalRowCount - leads.length }} more</div>
          }
        } @else {
          <!-- Individual leads review (paste / AI discovery) -->
          <h6 class="text-muted mb-2">Leads Found ({{ leads.length }})</h6>
          @for (lead of leads; track $index) {
            <div class="ws-item-card selected">
              <div class="ws-item-content">
                <div class="ws-item-name">{{ lead.name || 'Unknown' }} <span class="text-muted ms-2">{{ lead.phone }}</span></div>
                @if (lead.address) {
                  <div class="ws-item-meta">{{ lead.address }}</div>
                }
                <div class="ws-item-meta">
                  @switch (lead.status) {
                    @case ('pending') { <span class="text-muted">Ready to import</span> }
                    @case ('creating') { <span class="text-primary"><span class="spinner-border spinner-border-sm me-1"></span>Creating...</span> }
                    @case ('created') { <span class="text-success"><i class="bi bi-check-circle me-1"></i>Created</span> }
                    @case ('error') { <span class="text-danger"><i class="bi bi-exclamation-circle me-1"></i>Failed</span> }
                  }
                </div>
              </div>
              @if (lead.status === 'pending') {
                <button class="btn btn-sm btn-link text-danger" (click)="removeLead(lead)"><i class="bi bi-x-lg"></i></button>
              }
            </div>
          }
        }

        @if (!leads.length && uploadSource !== 'file') {
          <div class="ws-empty-state">
            <i class="bi bi-person-plus"></i>
            <p>No leads to import.</p>
          </div>
        }

        <div class="d-flex gap-2 mt-3">
          <button class="btn btn-outline-secondary" (click)="backToChoose()">
            <i class="bi bi-arrow-left me-1"></i>Try another option
          </button>
        </div>
      }
    }

    <div class="ws-save-row">
      <button class="btn btn-link text-muted" (click)="skip()">I'll add leads later</button>
      @if (mode === 'review' && (uploadSource === 'file' ? rawRows.length > 0 : leads.length > 0)) {
        <button mat-raised-button color="primary" (click)="importLeads()" [disabled]="isImporting">
          @if (isImporting) {
            <span class="spinner-border spinner-border-sm me-1"></span>
          }
          @if (uploadSource === 'file') {
            Import {{ totalRowCount > 100 ? 100 : totalRowCount }} Lead(s) & Continue
          } @else {
            Import {{ leads.length }} Lead(s) & Continue
          }
        </button>
      }
    </div>
  </div>
</div>
